define('modules/editor/models/page',['require','exports','module','backbone','../collections/sections','../models/elements/section','../collections/page_styles','../collections/page_scripts','./landing_seo','./pagestate','./editorstate','lib/util','underscore'],function(require, exports, module) {
  var Backbone = require("backbone"),
      Sections = require("../collections/sections"),
      Section = require("../models/elements/section"),
      StylesCollection = require("../collections/page_styles"),
      ScriptsCollection = require("../collections/page_scripts"),
      SeoModel = require("./landing_seo"),
      PageState = require("./pagestate"),
      EditorState = require("./editorstate"),
      ActiveItemState = EditorState.activeItemState,
      util = require("lib/util"),
      _ = require("underscore");

  /**
   * Page Model - It represents an HTML page created with the editor
   * Each LPG has 2 Pages: the content (form) and the confirmation
   */

  /**
   * Serialization methods
   * @type {Object}
   */
  var Serialization = {
    /**
     * Parses the server response
     * @param  {object} json JSON data
     * @return {object}      Serialized data
     */
    parse: function(json) {
      //Reset instead of re-create
      this.sections.reset(Sections.fromJSON(json.sections));
      this.styles.reset(json.styles || []);
      this.scripts.reset(json.scripts || []);
      if (_.isObject(json.seo)) {
        this.seoParameters.set(_.omit(json.seo, 'customMeta'));
        this.seoParameters.customMeta.reset(json.seo.customMeta);
      }
      var pageJson = _.omit(json, ["sections", "styles", "scripts", "seo"]);
      //if we parsed a form, notify it
      var form = this.hasElement("element:form");

      if (form) {
        this.trigger("form:added", form);
      }

      //base Backbone.Model parse
      return module.exports.__super__.parse.call(this, pageJson);
    },
    /**
     * Returns the JSON representation of a Page
     * @return {object} JSON data
     */
    toJSON: function() {
      var originalJSON = module.exports.__super__.toJSON.call(this);
      return _.extend(
        _.omit(originalJSON, "html", "role", "step", "id", "meta"), {
          sections: this.sections.toJSON(),
          styles: this.styles.toJSON(),
          scripts: this.scripts.toJSON(),
          seo: this.seoParameters.toJSON()
        }
      );
    }
  };

  /**
   * Page Model methods
   * @type {Object}
   */
  var Controller = {
    /**
     * Returns the number of sections on this page
     * @return {integer}
     */
    getSectionCount: function() {
      return this.sections.length;
    },
    /**
     * Adds a new section at end of the page
     */
    addNewSection: function() {
      this.sections.add({});
      this.trigger("section:added");
    },
    /**
     * Adds a new section before or after another section
     * @param {Section}  ref Reference section
     * @param {Boolean} isAfter Insert after or before reference section
     */
    addNewSectionAt: function(ref, isAfter) {
      if (!ref)
        this.sections.add({});
      else if (isAfter)
        this.sections.addAfter(ref, {});
      else
        this.sections.addBefore(ref, {});

      this.trigger("section:added");
    },
    /**
     * Moves a section before or after another section
     * @param  {Section}  ref
     * @param  {Section}  section
     * @param  {Boolean} isAfter
     */
    moveSection: function(ref, section, isAfter) {
      this.deselectItem();
      if (isAfter) {
        this.sections.moveAfter(ref, section);
      } else {
        this.sections.moveBefore(ref, section);
      }
      this.selectItem(section);
    },
    /**
     * Moves the currently selected section one position higher in the page
     */
    moveSectionUp: function() {
      var section = ActiveItemState.get("item");
      if (!section || !section.elements) {
        return;
      }
      var index = this.sections.indexOf(section);
      if (index > 0) {
        var previousSection = this.sections.at(index-1);
        this.moveSection(previousSection, section, false);
      }
    },
    /**
     * Moves the currently selected section one position lower in the page
     */
    moveSectionDown: function() {
      var section = ActiveItemState.get("item");
      if (!section || !section.elements) {
        return;
      }
      var index = this.sections.indexOf(section);
      if (index < this.sections.length-1) {
        var nextSection = this.sections.at(index+1);
        this.moveSection(nextSection, section, true);
      }
    },
    /**
    * Modifies every section's width
    */
    adjustSectionWidth: function(inc) {
      this.sections.each(function(section) {
        section.adjustWidth(inc);
      });
    },
    /**
     * Selects an item
     * @param  {Model} item Element model being selected
     */
    selectItem: function(item) {
      ActiveItemState.selectItem(item);
    },
    /**
     * Gets the currently selected item
     * @return {Model} The selected element model
     */
    getSelectedItem: function() {
      return ActiveItemState.getActiveItem();
    },
    /**
     * De-selects the current active item
     */
    deselectItem: function() {
      ActiveItemState.deselect();
    },
    /**
     * Deletes the current selected item
     */
    deleteSelectedItem: function() {
      var element = ActiveItemState.getActiveItem();
      EditorState.deselect();
      this.deleteItem(element.formModel || element);
    },
    /**
     * Deletes a specifed item
     */
    deleteItem: function(element) {
      var section = this.sections.findSectionContaining(element);

      if (element) {
        element.triggerMethod("before:remove");
        if (element.name === "element:form") {
          this.trigger("form:removed", element);
        }
        if (element.name === "section") {
          //is there a form in that section?
          //notify we've deleted a section
          this.trigger("section:removed");
        }
        if (section) {
          section.removeElement(element, true);
        }
        else {
          //kill the section
          this.sections.remove(element);
        }
        // Elements tree is listening this to rerender
        this.trigger("element:removed");
        EditorState.setVariantChanged(true);
      }
    },
    /**
     * Duplicates the currently selected item
     */
    duplicateSelectedItem: function() {
      var element = ActiveItemState.get("item"),
          newElement = element && element.selfClone(),
          isSection = (element && element.defaultNameKey === "section");
      //set (override) position
      var originalPos = element.getPosition();
      newElement.setPosition({x: parseInt(originalPos.x) + 30, y: parseInt(originalPos.y) + 30});
      if (isSection) {
        this.sections.addAfter(element, newElement);
      }
      else if (newElement) {
        var container = this.sections.findContainerFor(element);
        if (container) container.addElement(newElement);
      }
      this.selectItem(newElement);
    },
    /**
     * Adds an element to a Section
     * @param {Model} section  Target section model
     * @param {Model} element  Element model
     * @param {object} position Element's pos within the section
     */
    addElementToSection: function(section, element, position) {
      this.deselectItem();
      this.sections.addElementToSection(section, element, position);
      this.selectItem(element);
    },
    /**
     * Moves an element into a Section
     * @param  {Model} container  Target section model
     * @param  {Model} element  Element model
     * @param  {object} position Element's position within the section
     */
    moveElementToContainer: function(container, element, position) {
      this.deselectItem();
      this.sections.moveElementToContainer(container, element, position);
      this.selectItem(element);
    },

    /**
     * Moves the selected element up (forward), higher z-index
     */
    moveUpElement: function() {
      var element = ActiveItemState.get("item");
      element = element.formModel || element;
      if (element && element.name && element.collection) {
        var section = element.collection;
        section.moveElementForward(element);
      }
    },

    /**
     * Moves the selected element down (backward), lower z-index
     */
    moveDownElement: function() {
      var element = ActiveItemState.get("item");
      element = element.formModel || element;
      if (element && element.name && element.collection) {
        var section = element.collection;
        section.moveElementBackward(element);
      }
    },

    /**
     * Checks whether this page contains a given element
     * @param  {Model}  elementName Element to check
     * @return {Boolean}             True if the element is here, false otherwise
     */
    hasElement: function(elementName) {
      return this.sections.hasElement(elementName);
    },
    getElementById: function(id) {
      return this.sections.reduce(function(element, section) {
        return element || section.getElementById(id);
      }, undefined);
    },
    getHighestId: function() {
      return this.sections.reduce(function(id, section) {
        return Math.max(id, section.getHighestId());
      }, 0);
    },
    /**
     * Triggered after an element is added to the page
     * @param  {Model} model Added element's model
     */
    _handleElementAdded: function(model) {
      //notifiy the element added for the tree generation
      this.trigger('element:added', model);

      //is this a section?
      if (model.name === "section") {
        this.trigger("section:added", model);
        return;
      }

      //if element is a form, notify it
      if (model.name === "element:form") {
        this.trigger("form:added", model);
        //and open the form editor if it's new
        if (model.getPropertyGroup("form:content").get("content") === "") {
          this.collection.services.request("form:content", model.toJSON,
            _.bind(model.saveFormEditorData, model));
        }
        return;
      }

      //for HTML, open the pop up
      if (model.name === "element:html") {
        model.editHtml();
      }

      //if element is an image, open the pop up
      if (model.name === "element:image" && model.getPropertyGroup("image:content").get("imageUrl") === "") {

        var container = this.sections.findSectionContaining(model);
        this.collection.services.request("image:content",
          model.getPropertyGroup("image:content").get("imageUrl"),
          function(results) {
            model.trigger("image:upload:start");
            // silently setting the image props, until we know the computed image size
            model.getPropertyGroup("image:content").set(
            {
              "imageUrl": results.url,
              "imageId": results.id
            }, {silent: true});
            //model.getPropertyGroup("image:content").getImageSize();
            model.getPropertyGroup("image:content").adjustImageSize(container);
          });
        return;
      }

      //if element is a video, open the pop up
      if (model.name === "element:video" && !model.hasContent()) {
        this.collection.services.request("video:content",
          model.get("content"),
          function(content) {
            model.saveData(content);
          });
        return;
      }

      //for Popup, open the pop up
      if (model.name === "element:popup") {
        model.editPopup();
      }
    },

    getActiveSocialPlugins: function() {
      return _.union.apply(_, this.sections.invoke("getActiveSocialPlugins"));
    },
    selfClone: function(options) {
      return this.sections.map(function(section) {
        return section.selfClone(options);
      });
    },
    setupFormConversion: function() {
      var form = this.sections.hasElement("element:form"),
          formConversionPg = form && form.getPropertyGroup("form:conversion"),
          pageConversionPg = this.getVariant().getPropertyGroup("page:conversion"),
          step = this.get("step");
      if (form && pageConversionPg.get) {
        formConversionPg.set("step", step);
        formConversionPg.set("executeConversion", pageConversionPg.get("executionStep") == step);
      }
    },
    editStyles: function() {
      //we send a .clone() of the actual styles in case the user wants to
      //discard changes
      this.collection.services.request("styles:content",
                            this.styles.clone(),
                            _.bind(this._saveStyles, this));
    },
    _saveStyles: function(newStyles, options) {
      this.styles.reset(newStyles);
      if (!options.silent) this.trigger("page:change:styles", newStyles);
    },
    editScripts: function() {
      this.collection.services.request("scripts:content",
                            this.scripts.clone(),
                            _.bind(this._saveScripts, this));
    },
    _saveScripts: function(newScripts, options) {
      this.scripts.reset(newScripts);
      if (!options.silent) this.trigger("page:change:scripts");
    },
    editSEO: function() {
      this.collection.services.request("seo:content",
                            this.seoParameters.clone(),
                            _.bind(this._saveSEO, this));
    },
    _saveSEO: function(newSeo, options) {
      this.seoParameters.set(newSeo.attributes);
      this.seoParameters.customMeta.reset(newSeo.customMeta.toJSON());
      if (!options.silent) this.trigger("page:change:seo");
    }
  };

  module.exports = Backbone.Model.extend({
    defaults: {
      html: ""
    },
    //elements not available for adding to this model (element:XXXX => only use XXXX)
    invalidElements: [],
    constructor: function(data, options) {
      //The page's Section Collection
      this.sections = new Sections();
      //Page state, holds HTML5 drag & drop state
      this.state = new PageState();

      this.styles = new StylesCollection();
      this.scripts = new ScriptsCollection();
      this.seoParameters = new SeoModel();

      module.exports.__super__.constructor.apply(this,arguments);
    },
    initialize: function(attributes, options) {
      this.listenTo(this.sections, "add:element", function(model) {
        this._handleElementAdded(model);
      });

      this.listenTo(this.sections, "remove:element", function(model) {
        if (model.name === "element:form") {
          this.trigger("form:removed", model);
        }
      });

      this.listenTo(this, "form:added change:step", this.setupFormConversion);

      this.listenTo(this.sections, "add", function(newSection) {
        //for new sections, configure it with the default Page width
        var pageWidth = this.getVariant().getPropertyGroup("page:defaults").get("width");
        if (pageWidth) {
          newSection.getPropertyGroup("sectionGeometry").set("width", pageWidth);
        }
      });

      this.listenTo(this, "form:removed", function(form) {
        form.toggleLegalTerms(false);
      });
    },
    getVariant: function() {
      return this.collection.variant;
    }
  });

  util.mixin(module.exports, Serialization);
  util.mixin(module.exports, Controller);

});

